import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@components/common/Modal/Modal";
import ConfirmationModal from "@components/common/Modal/ConfirmationModal";
import InputField from "@components/common/InputField/components/InputField";
import Button from "@components/common/Button/Button";
import { InlineSpinner } from "@components/common/Spinner";
import EmploymentFormDropdowns from "./EmploymentFormDropdowns";
import EmploymentLocationDropdowns from "./EmploymentLocationDropdowns";
import type { StatusModalProps, StatusFormData, EmployerFormData } from "../types/status.types";
import { getStatusEntityTitle, getStatusModalTitle } from "../utils/statusI18n";
import {
	useEmploymentActivities,
	useHigherStudies,
	useStudentOutcomeCurrent,
	useEmployers,
	useOutcomeJobTypes,
	useCountries,
	useDistricts,
	useCities,
	type EmploymentActivityType,
} from "../services/queries";
import {
	useCreateEmploymentActivity,
	useUpdateEmploymentActivity,
	useDeleteEmploymentActivity,
	useCreateHigherStudy,
	useUpdateHigherStudy,
	useDeleteHigherStudy,
	useUpdateStudentOutcome,
} from "../services/mutations";
import {
	fetchEmploymentActivityById,
	type EmploymentActivity,
	type HigherStudy,
} from "../services/statusService";
import { Edit, Add, Check, Close, Delete } from "@mui/icons-material";
import { PORTAL_STATUS_MODAL_WIDTH_CLASS } from "@constants/portalUiTheme";
import {
	buildStudentEmploymentPayload,
	createEmptyEmployerFormData,
	employmentToFormData,
	findPakistanCountryOption,
	getEmploymentPayloadType,
	higherStudyToFormData,
	isPakistanCountry,
	isValidContactNumber,
	isValidEmploymentDateRange,
	parseStudentEmployment,
} from "../utils/statusUtils";

const isNonEmpty = (value?: string) => Boolean(value && value.trim() !== "");
const isValidEmail = (value?: string) => Boolean(value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()));
const isPositiveNumber = (value?: string) => {
	if (!isNonEmpty(value)) return false;
	const num = Number.parseFloat((value ?? "").split(",").join(""));
	return Number.isFinite(num) && num > 0;
};

const buildEmploymentActivitiesSignature = (items: EmploymentActivity[]) =>
	JSON.stringify(
		items.map(item => ({
			uuid: item.uuid,
			updatedAt: item.updatedAt,
			organizationName: item.organizationName,
			roleTitle: item.roleTitle,
			monthlyIncome: item.monthlyIncome,
			salary: item.salary,
			contactPersonName: item.contactPersonName,
			contactPersonNumber: item.contactPersonNumber,
			contactPersonEmail: item.contactPersonEmail,
			startDate: item.startDate,
			endDate: item.endDate ?? item.jobEnded,
			employerBusinessDetailsId: item.employerBusinessDetailsId,
			outcomeJobTypeUuid: item.outcomeJobType?.uuid,
			countryUuid: item.country?.uuid,
			districtUuid: item.district?.uuid,
			cityUuid: item.city?.uuid,
		})),
	);

const buildHigherStudiesSignature = (items: HigherStudy[]) =>
	JSON.stringify(
		items.map(item => ({
			uuid: item.uuid,
			updatedAt: item.updatedAt,
			instituteName: item.instituteName,
			degreeName: item.degreeName,
			contactPersonName: item.contactPersonName,
			contactPersonNumber: item.contactPersonNumber,
		})),
	);

const StatusModal: React.FC<StatusModalProps> = ({
	isOpen,
	onClose,
	statusType,
	statusId,
	statusApiCode,
	onStatusUpdated,
}) => {
	const { t } = useTranslation();
	const [formDataList, setFormDataList] = useState<EmployerFormData[]>([]);
	const [editingIndex, setEditingIndex] = useState<number | null>(null);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
	const [employerSearchTerm, setEmployerSearchTerm] = useState("");
	const [jobTypeSearchTerm, setJobTypeSearchTerm] = useState("");
	const [countrySearchTerm, setCountrySearchTerm] = useState("");
	const [districtSearchTerm, setDistrictSearchTerm] = useState("");
	const [citySearchTerm, setCitySearchTerm] = useState("");

	const lastDataSignatureRef = useRef<string>("");
	const isInitializedRef = useRef(false);
	const isSavingRef = useRef(false);
	const formListRef = useRef<HTMLDivElement>(null);
	const scrollToTopOnAddRef = useRef(false);

	const isHigherStudies = statusType === "Higher Studies";
	const isEmploymentForm = statusType === "Employed" || statusType === "Self Employed";
	const isSelfEmployed = statusType === "Self Employed";
	const employmentActivityType: EmploymentActivityType | null = getEmploymentPayloadType(statusApiCode);

	const {
		employmentActivities,
		loading: loadingEmployment,
		refetch: refetchEmploymentActivities,
	} = useEmploymentActivities(employmentActivityType, isOpen && isEmploymentForm);

	const {
		higherStudies,
		loading: loadingHigherStudies,
		refetch: refetchHigherStudies,
	} = useHigherStudies(isOpen && isHigherStudies);

	const { refetch: refetchOutcomeCurrent } = useStudentOutcomeCurrent(isOpen);
	const updateStudentOutcomeMutation = useUpdateStudentOutcome();

	const createEmploymentMutation = useCreateEmploymentActivity();
	const updateEmploymentMutation = useUpdateEmploymentActivity();
	const deleteEmploymentMutation = useDeleteEmploymentActivity();
	const createHigherStudyMutation = useCreateHigherStudy();
	const updateHigherStudyMutation = useUpdateHigherStudy();
	const deleteHigherStudyMutation = useDeleteHigherStudy();

	const {
		employerOptions,
		loadingEmployers,
		fetchNextEmployers,
		hasNextEmployers,
		isFetchingNextEmployers,
	} = useEmployers(employerSearchTerm, isOpen && statusType === "Employed");

	const {
		jobTypeOptions,
		loadingJobTypes,
		fetchNextJobTypes,
		hasNextJobTypes,
		isFetchingNextJobTypes,
	} = useOutcomeJobTypes(statusId, jobTypeSearchTerm, isOpen && isEmploymentForm && !!statusId);

	const {
		countryOptions,
		loadingCountries,
		fetchNextCountries,
		hasNextCountries,
		isFetchingNextCountries,
	} = useCountries(countrySearchTerm, isOpen && isEmploymentForm);

	const { countryOptions: pakistanCountryOptions } = useCountries(
		"Pakistan",
		isOpen && isEmploymentForm,
	);

	const defaultPakistanCountry = useMemo(
		() =>
			findPakistanCountryOption(countryOptions) ?? findPakistanCountryOption(pakistanCountryOptions),
		[countryOptions, pakistanCountryOptions],
	);

	const shouldLoadDistrictCity = useMemo(
		() =>
			isOpen &&
			isEmploymentForm &&
			formDataList.some(form => isPakistanCountry(form.countryUuid, countryOptions, form.countryName)),
		[isOpen, isEmploymentForm, formDataList, countryOptions],
	);

	const {
		districts,
		districtOptions,
		loadingDistricts,
		fetchNextDistricts,
		hasNextDistricts,
		isFetchingNextDistricts,
	} = useDistricts(districtSearchTerm, shouldLoadDistrictCity);

	const activeDistrictId = useMemo(() => {
		if (editingIndex === null) return undefined;
		const form = formDataList[editingIndex];
		if (form?.districtId) return form.districtId;
		if (!form?.districtUuid) return undefined;
		const district = districts.find(item => item.uuid === form.districtUuid);
		return district ? String(district.id) : undefined;
	}, [editingIndex, formDataList, districts]);

	const {
		cityOptions,
		loadingCities,
		fetchNextCities,
		hasNextCities,
		isFetchingNextCities,
	} = useCities(activeDistrictId, citySearchTerm, shouldLoadDistrictCity && !!activeDistrictId);

	const isLoading = isHigherStudies ? loadingHigherStudies : isEmploymentForm ? loadingEmployment : false;
	const employerFieldLabel = getStatusEntityTitle(t, isSelfEmployed ? "Self Employed" : "Employed");

	useEffect(() => {
		if (!isOpen) {
			setFormDataList([]);
			setEditingIndex(null);
			setDeleteModalOpen(false);
			setDeleteIndex(null);
			setEmployerSearchTerm("");
			setJobTypeSearchTerm("");
			setCountrySearchTerm("");
			setDistrictSearchTerm("");
			setCitySearchTerm("");
			lastDataSignatureRef.current = "";
			isInitializedRef.current = false;
		}
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen || isLoading) return;

		if (isHigherStudies) {
			const signature = buildHigherStudiesSignature(higherStudies);
			if (lastDataSignatureRef.current === signature && isInitializedRef.current) return;

			lastDataSignatureRef.current = signature;
			isInitializedRef.current = true;
			setFormDataList(higherStudies.map(higherStudyToFormData));
			setEditingIndex(null);
			return;
		}

		if (isEmploymentForm) {
			const signature = buildEmploymentActivitiesSignature(employmentActivities);
			if (lastDataSignatureRef.current === signature && isInitializedRef.current) return;

			lastDataSignatureRef.current = signature;
			isInitializedRef.current = true;
			setFormDataList(
				employmentActivities.map(item =>
					employmentToFormData(item, statusType as "Employed" | "Self Employed"),
				),
			);
			setEditingIndex(null);
		}
	}, [isOpen, isLoading, isHigherStudies, isEmploymentForm, higherStudies, employmentActivities, statusType]);

	const handleInputChange = useCallback((index: number, field: keyof StatusFormData, value: string) => {
		setFormDataList(prev => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
	}, []);

	const handleEmployerChange = useCallback((index: number, uuid: string, businessName: string) => {
		setFormDataList(prev =>
			prev.map((item, i) =>
				i === index
					? {
							...item,
							employerBusinessDetailsId: uuid,
							organizationName: businessName,
						}
					: item,
			),
		);
	}, []);

	const handleJobTypeChange = useCallback((index: number, uuid: string) => {
		setFormDataList(prev =>
			prev.map((item, i) => (i === index ? { ...item, outcomeJobTypeUuid: uuid } : item)),
		);
	}, []);

	const handleDistrictChange = useCallback(
		(index: number, uuid: string, districtName: string) => {
			const district = districts.find(item => item.uuid === uuid);
			setFormDataList(prev =>
				prev.map((item, i) =>
					i === index
						? {
								...item,
								districtUuid: uuid,
								districtName,
								districtId: district ? String(district.id) : "",
								cityUuid: "",
								cityName: "",
							}
						: item,
				),
			);
			setCitySearchTerm("");
		},
		[districts],
	);

	const handleCityChange = useCallback((index: number, uuid: string, cityName: string) => {
		setFormDataList(prev =>
			prev.map((item, i) => (i === index ? { ...item, cityUuid: uuid, cityName } : item)),
		);
	}, []);

	const handleCountryChange = useCallback(
		(index: number, uuid: string, countryName: string) => {
			const isPakistan = isPakistanCountry(uuid, countryOptions, countryName);
			setFormDataList(prev =>
				prev.map((item, i) =>
					i === index
						? {
								...item,
								countryUuid: uuid,
								countryName,
								districtUuid: isPakistan ? item.districtUuid : "",
								districtName: isPakistan ? item.districtName : "",
								districtId: isPakistan ? item.districtId : "",
								cityUuid: isPakistan ? item.cityUuid : "",
								cityName: isPakistan ? item.cityName : "",
							}
						: item,
				),
			);
		},
		[countryOptions],
	);

	const handleAddNew = useCallback(() => {
		scrollToTopOnAddRef.current = true;
		setFormDataList(prev => [createEmptyEmployerFormData(defaultPakistanCountry), ...prev]);
		setEditingIndex(0);
	}, [defaultPakistanCountry]);

	useEffect(() => {
		if (!isEmploymentForm || !defaultPakistanCountry) return;

		setFormDataList(prev => {
			let changed = false;
			const next = prev.map(form => {
				if (form.isNew && !form.countryUuid) {
					changed = true;
					return {
						...form,
						countryUuid: defaultPakistanCountry.value,
						countryName: defaultPakistanCountry.label,
					};
				}
				return form;
			});
			return changed ? next : prev;
		});
	}, [isEmploymentForm, defaultPakistanCountry]);

	useEffect(() => {
		if (!scrollToTopOnAddRef.current) return;
		scrollToTopOnAddRef.current = false;
		formListRef.current?.scrollTo({ top: 0, behavior: "smooth" });
	}, [formDataList]);

	const handleEdit = useCallback(
		async (index: number) => {
			const item = formDataList[index];
			if (!item) return;

			if (isEmploymentForm && item.uuid && !item.isNew) {
				try {
					const response = await fetchEmploymentActivityById(item.uuid);
					const employment = parseStudentEmployment(response);
					setFormDataList(prev =>
						prev.map((form, i) =>
							i === index
								? employmentToFormData(employment, statusType as "Employed" | "Self Employed")
								: form,
						),
					);
				} catch {
					// Keep list data if single-record fetch fails.
				}
			}

			setEditingIndex(index);
		},
		[formDataList, isEmploymentForm, statusType],
	);

	const handleCancelEdit = useCallback(
		(index: number) => {
			const item = formDataList[index];
			if (item?.isNew) {
				setFormDataList(prev => prev.filter((_, i) => i !== index));
			} else if (isHigherStudies) {
				setFormDataList(higherStudies.map(higherStudyToFormData));
			} else if (isEmploymentForm) {
				setFormDataList(
					employmentActivities.map(activity =>
						employmentToFormData(activity, statusType as "Employed" | "Self Employed"),
					),
				);
			}
			setEditingIndex(null);
		},
		[formDataList, isHigherStudies, isEmploymentForm, higherStudies, employmentActivities, statusType],
	);

	const isFormValid = useCallback(
		(formData: EmployerFormData) => {
			if (statusType === "Employed" || statusType === "Self Employed") {
				const isPakistan = isPakistanCountry(formData.countryUuid, countryOptions, formData.countryName);
				const hasEmployerOrBusiness = isSelfEmployed
					? isNonEmpty(formData.organizationName)
					: isNonEmpty(formData.employerBusinessDetailsId);

				return (
					hasEmployerOrBusiness &&
					isNonEmpty(formData.outcomeJobTypeUuid) &&
					isNonEmpty(formData.contactPerson) &&
					isValidContactNumber(formData.contactNumber) &&
					isNonEmpty(formData.contactEmail) &&
					isValidEmail(formData.contactEmail) &&
					isNonEmpty(formData.designation) &&
					isNonEmpty(formData.startingDate) &&
					isNonEmpty(formData.endDate) &&
					isValidEmploymentDateRange(formData.startingDate, formData.endDate) &&
					isNonEmpty(formData.countryUuid) &&
					(!isPakistan || (isNonEmpty(formData.districtUuid) && isNonEmpty(formData.cityUuid))) &&
					isPositiveNumber(statusType === "Employed" ? formData.monthlySalary : formData.monthlyIncome)
				);
			}
			return (
				isNonEmpty(formData.universityName) &&
				isNonEmpty(formData.contactPerson) &&
				isValidContactNumber(formData.contactNumber) &&
				isNonEmpty(formData.degree)
			);
		},
		[statusType, isSelfEmployed, countryOptions],
	);

	const refreshStatusData = useCallback(async () => {
		if (isHigherStudies) {
			await refetchHigherStudies();
		} else if (isEmploymentForm) {
			await refetchEmploymentActivities();
		}
		await refetchOutcomeCurrent();
		isInitializedRef.current = false;
		lastDataSignatureRef.current = "";
	}, [
		isHigherStudies,
		isEmploymentForm,
		refetchHigherStudies,
		refetchEmploymentActivities,
		refetchOutcomeCurrent,
	]);

	const handleSave = useCallback(
		async (index: number) => {
			if (isSavingRef.current) return;

			const formData = formDataList[index];
			if (!formData) return;

			isSavingRef.current = true;

			try {
				if (isHigherStudies) {
					const payload = {
						instituteName: formData.universityName || "",
						degreeName: formData.degree || "",
						contactPersonName: formData.contactPerson || "",
						contactPersonNumber: formData.contactNumber || "",
					};
					const isNewRecord = formData.isNew || !formData.uuid;

					if (isNewRecord) {
						await createHigherStudyMutation.mutateAsync(payload);
					} else {
						await updateHigherStudyMutation.mutateAsync({ uuid: formData.uuid!, payload });
					}

					if (statusId) {
						const currentDate = new Date().toISOString().split("T")[0];
						await updateStudentOutcomeMutation.mutateAsync({
							outcomeStatusId: statusId,
							effectiveFrom: currentDate,
							remarks: "",
							suppressSuccessNotification: true,
						});
					}

					await refreshStatusData();
					setEditingIndex(null);
					return;
				}

				if (!statusId) return;

				const payload = buildStudentEmploymentPayload(
					formData,
					statusType as "Employed" | "Self Employed",
					statusId,
					statusApiCode,
					countryOptions,
				);
				if (!payload) return;

				const isNewRecord = formData.isNew || !formData.uuid;

				if (isNewRecord) {
					await createEmploymentMutation.mutateAsync(payload);
					onStatusUpdated?.(statusType);
				} else {
					await updateEmploymentMutation.mutateAsync({ uuid: formData.uuid!, payload });
				}

				await refreshStatusData();
				setEditingIndex(null);
			} catch {
				// Errors handled by mutation callbacks
			} finally {
				isSavingRef.current = false;
			}
		},
		[
			formDataList,
			isHigherStudies,
			statusType,
			statusId,
			statusApiCode,
			countryOptions,
			createEmploymentMutation,
			updateEmploymentMutation,
			createHigherStudyMutation,
			updateHigherStudyMutation,
			updateStudentOutcomeMutation,
			refreshStatusData,
			onStatusUpdated,
		],
	);

	const handleDeleteClick = useCallback((index: number) => {
		setDeleteIndex(index);
		setDeleteModalOpen(true);
	}, []);

	const handleDeleteConfirm = useCallback(async () => {
		if (deleteIndex === null) return;

		const formData = formDataList[deleteIndex];
		if (!formData) return;

		if (formData.isNew || !formData.uuid) {
			setFormDataList(prev => prev.filter((_, i) => i !== deleteIndex));
			setDeleteModalOpen(false);
			setDeleteIndex(null);
			setEditingIndex(null);
			return;
		}

		if (isHigherStudies) {
			await deleteHigherStudyMutation.mutateAsync(formData.uuid).catch(() => {});
		} else {
			await deleteEmploymentMutation.mutateAsync(formData.uuid).catch(() => {});
		}

		await refreshStatusData();
		setEditingIndex(null);
		setDeleteModalOpen(false);
		setDeleteIndex(null);
	}, [
		deleteIndex,
		formDataList,
		isHigherStudies,
		deleteEmploymentMutation,
		deleteHigherStudyMutation,
		refreshStatusData,
	]);

	const handleDeleteCancel = useCallback(() => {
		setDeleteModalOpen(false);
		setDeleteIndex(null);
	}, []);

	const isSaving =
		createEmploymentMutation.isPending ||
		updateEmploymentMutation.isPending ||
		createHigherStudyMutation.isPending ||
		updateHigherStudyMutation.isPending ||
		updateStudentOutcomeMutation.isPending;
	const isDeleting = deleteEmploymentMutation.isPending || deleteHigherStudyMutation.isPending;
	const hasLoaded = isInitializedRef.current && !isLoading;

	const title = useMemo(() => getStatusEntityTitle(t, statusType), [statusType, t]);
	const modalTitle = useMemo(() => getStatusModalTitle(t, statusType), [statusType, t]);
	const titleLower = title.toLocaleLowerCase();

	const renderFormFields = useCallback(
		(formData: EmployerFormData, index: number, disabled: boolean) => {
			const commonProps = (id: string, label: string, field: keyof StatusFormData, placeholder: string) => ({
				id: `${id}-${index}`,
				label,
				type: "text" as const,
				placeholder,
				value: (formData[field] as string) || "",
				onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(index, field, e.target.value),
				required: true,
				disabled,
			});

			const contactNumber = formData.contactNumber || "";
			const hasContactNumberError = isNonEmpty(contactNumber) && !isValidContactNumber(contactNumber);
			const contactNumberFieldProps = {
				id: `contact-number-${index}`,
				label: t("feature.status.form.contactNumber"),
				type: "tel" as const,
				placeholder: t("feature.status.form.contactNumberPlaceholder"),
				value: contactNumber,
				onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
					handleInputChange(index, "contactNumber", e.target.value),
				required: true,
				disabled,
				error: hasContactNumberError,
				helperText: hasContactNumberError ? t("feature.status.form.contactNumberInvalid") : undefined,
			};

			const contactEmail = formData.contactEmail || "";
			const hasContactEmailError = isNonEmpty(contactEmail) && !isValidEmail(contactEmail);
			const contactEmailFieldProps = {
				id: `contact-email-${index}`,
				label: t("feature.status.form.contactPersonEmail"),
				type: "email" as const,
				placeholder: t("feature.status.form.contactEmailPlaceholder"),
				value: contactEmail,
				onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
					handleInputChange(index, "contactEmail", e.target.value),
				required: true,
				disabled,
				error: hasContactEmailError,
				helperText: hasContactEmailError ? t("feature.status.form.contactEmailInvalid") : undefined,
			};

			if (statusType === "Employed" || statusType === "Self Employed") {
				const incomeField = statusType === "Employed" ? "monthlySalary" : "monthlyIncome";
				const incomeLabel =
					statusType === "Employed"
						? t("feature.status.form.monthlySalary")
						: t("feature.status.form.monthlyIncome");
				const startingDate = formData.startingDate || "";
				const endDate = formData.endDate || "";
				const hasDateRangeError =
					isNonEmpty(startingDate) &&
					isNonEmpty(endDate) &&
					!isValidEmploymentDateRange(startingDate, endDate);

				return (
					<>
						<InputField {...contactNumberFieldProps} />
						<InputField
							{...commonProps(
								"contact-person",
								t("feature.status.form.contactPerson"),
								"contactPerson",
								t("feature.status.form.contactPersonPlaceholder"),
							)}
						/>
						<InputField {...contactEmailFieldProps} />
						<InputField
							{...commonProps(
								"designation",
								t("feature.status.form.designation"),
								"designation",
								t("feature.status.form.designationPlaceholder"),
							)}
						/>
						<InputField
							{...commonProps(incomeField, incomeLabel, incomeField, t("feature.status.form.incomePlaceholder"))}
						/>
						<InputField
							id={`starting-date-${index}`}
							label={t("feature.status.form.startingDate")}
							type="date"
							placeholder={t("feature.status.form.selectDate")}
							value={startingDate}
							onChange={e => handleInputChange(index, "startingDate", e.target.value)}
							required
							disabled={disabled}
							max={endDate || undefined}
							error={hasDateRangeError}
							helperText={hasDateRangeError ? t("feature.status.form.dateRangeInvalid") : undefined}
						/>
						<InputField
							id={`end-date-${index}`}
							label={t("feature.status.form.endDate")}
							type="date"
							placeholder={t("feature.status.form.selectDate")}
							value={endDate}
							onChange={e => handleInputChange(index, "endDate", e.target.value)}
							required
							disabled={disabled}
							min={startingDate || undefined}
							error={hasDateRangeError}
							helperText={hasDateRangeError ? t("feature.status.form.dateRangeInvalid") : undefined}
						/>
					</>
				);
			}

			return (
				<>
					<div className="space-y-4">
						<InputField
							{...commonProps(
								"university-name",
								t("feature.status.form.universityName"),
								"universityName",
								t("feature.status.form.enterName"),
							)}
						/>
						<InputField
							{...commonProps(
								"contact-person",
								t("feature.status.form.contactPerson"),
								"contactPerson",
								t("feature.status.form.contactPersonPlaceholder"),
							)}
						/>
					</div>
					<div className="space-y-4">
						<InputField {...contactNumberFieldProps} />
						<InputField
							{...commonProps(
								"degree",
								t("feature.status.form.degree"),
								"degree",
								t("feature.status.form.degreePlaceholder"),
							)}
						/>
					</div>
				</>
			);
		},
		[statusType, handleInputChange, t],
	);

	const renderFormCard = useCallback(
		(formData: EmployerFormData, index: number) => {
			const isEditing = editingIndex === index;
			const disabled = !isEditing;
			const isNewRecord = Boolean(formData.isNew || !formData.uuid);
			const canInteract = editingIndex === null || editingIndex === index;

			return (
				<div
					key={formData.uuid || `new-${index}`}
					className={`mb-4 rounded-lg border p-4 ${isEditing ? "border-accent bg-accent-40" : "border-gray-200 bg-gray-50"}`}>
					<div className="mb-4 flex items-center justify-between">
						<h3 className="text-lg font-semibold text-gray-800">
							{isNewRecord
								? t("feature.status.actions.newEntity", { entity: title })
								: t("feature.status.actions.entityNumber", { entity: title, number: index + 1 })}
						</h3>
						<div className="flex gap-2">
							{isEditing ? (
								<>
									<button
										type="button"
										onClick={() => handleCancelEdit(index)}
										className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-red-100 hover:text-red-600"
										title={t("feature.status.actions.cancel")}
										disabled={isSaving || isDeleting}>
										<Close sx={{ fontSize: 18 }} />
									</button>
									<button
										type="button"
										onClick={() => handleSave(index)}
										className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent text-white transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
										title={isNewRecord ? t("feature.status.actions.save") : t("feature.status.actions.update")}
										disabled={!isFormValid(formData) || isSaving || isDeleting}
										aria-busy={isSaving}
									>
										{isSaving ? (
											<InlineSpinner size={16} onDark />
										) : (
											<Check sx={{ fontSize: 18 }} />
										)}
									</button>
								</>
							) : (
								<>
									<button
										type="button"
										onClick={() => handleEdit(index)}
										className="rounded-full p-2 text-gray-500 transition-colors hover:bg-accent-40 hover:text-accent disabled:opacity-50"
										title={t("feature.status.actions.edit")}
										disabled={!canInteract || isDeleting}>
										<Edit fontSize="small" />
									</button>
									<button
										type="button"
										onClick={() => handleDeleteClick(index)}
										className="rounded-full p-2 text-gray-500 transition-colors hover:bg-red-100 hover:text-red-600 disabled:opacity-50"
										title={t("feature.status.actions.delete")}
										disabled={!canInteract || isDeleting || isSaving}>
										<Delete fontSize="small" />
									</button>
								</>
							)}
						</div>
					</div>
					<div className="grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-2">
						{isEmploymentForm && (
							<>
								<EmploymentFormDropdowns
									index={index}
									employerLabel={employerFieldLabel}
									employerValue={formData.employerBusinessDetailsId || ""}
									organizationName={formData.organizationName || ""}
									useOrganizationNameInput={isSelfEmployed}
									jobTypeValue={formData.outcomeJobTypeUuid || ""}
									disabled={disabled}
									statusId={statusId}
									employerOptions={employerOptions}
									jobTypeOptions={jobTypeOptions}
									loadingEmployers={loadingEmployers}
									loadingJobTypes={loadingJobTypes}
									onEmployerChange={(uuid, businessName) => handleEmployerChange(index, uuid, businessName)}
									onOrganizationNameChange={value => handleInputChange(index, "organizationName", value)}
									onJobTypeChange={uuid => handleJobTypeChange(index, uuid)}
									onEmployerSearchChange={setEmployerSearchTerm}
									onJobTypeSearchChange={setJobTypeSearchTerm}
									fetchNextEmployers={fetchNextEmployers}
									fetchNextJobTypes={fetchNextJobTypes}
									hasNextEmployers={hasNextEmployers}
									hasNextJobTypes={hasNextJobTypes}
									isFetchingNextEmployers={isFetchingNextEmployers}
									isFetchingNextJobTypes={isFetchingNextJobTypes}
								/>
								{renderFormFields(formData, index, disabled)}
								<EmploymentLocationDropdowns
									index={index}
									disabled={disabled}
									showDistrictAndCity={isPakistanCountry(
										formData.countryUuid,
										countryOptions,
										formData.countryName,
									)}
									countryValue={formData.countryUuid || ""}
									countryName={formData.countryName}
									districtValue={formData.districtUuid || ""}
									districtName={formData.districtName}
									cityValue={formData.cityUuid || ""}
									cityName={formData.cityName}
									countryOptions={countryOptions}
									districtOptions={districtOptions}
									cityOptions={cityOptions}
									loadingCountries={loadingCountries}
									loadingDistricts={loadingDistricts}
									loadingCities={loadingCities}
									onCountryChange={(uuid, countryName) => handleCountryChange(index, uuid, countryName)}
									onDistrictChange={(uuid, districtName) => handleDistrictChange(index, uuid, districtName)}
									onCityChange={(uuid, cityName) => handleCityChange(index, uuid, cityName)}
									onCountrySearchChange={setCountrySearchTerm}
									onDistrictSearchChange={setDistrictSearchTerm}
									onCitySearchChange={setCitySearchTerm}
									fetchNextCountries={fetchNextCountries}
									fetchNextDistricts={fetchNextDistricts}
									fetchNextCities={fetchNextCities}
									hasNextCountries={hasNextCountries}
									hasNextDistricts={hasNextDistricts}
									hasNextCities={hasNextCities}
									isFetchingNextCountries={isFetchingNextCountries}
									isFetchingNextDistricts={isFetchingNextDistricts}
									isFetchingNextCities={isFetchingNextCities}
								/>
							</>
						)}
						{!isEmploymentForm && renderFormFields(formData, index, disabled)}
					</div>
				</div>
			);
		},
		[
			editingIndex,
			title,
			t,
			isSaving,
			isDeleting,
			handleCancelEdit,
			handleSave,
			handleEdit,
			handleDeleteClick,
			isFormValid,
			renderFormFields,
			isEmploymentForm,
			isSelfEmployed,
			employerFieldLabel,
			statusId,
			employerOptions,
			jobTypeOptions,
			loadingEmployers,
			loadingJobTypes,
			handleEmployerChange,
			handleJobTypeChange,
			countryOptions,
			districtOptions,
			cityOptions,
			loadingCountries,
			loadingDistricts,
			loadingCities,
			handleCountryChange,
			handleDistrictChange,
			handleCityChange,
			fetchNextEmployers,
			fetchNextJobTypes,
			hasNextEmployers,
			hasNextJobTypes,
			isFetchingNextEmployers,
			isFetchingNextJobTypes,
			fetchNextCountries,
			fetchNextDistricts,
			fetchNextCities,
			hasNextCountries,
			hasNextDistricts,
			hasNextCities,
			isFetchingNextCountries,
			isFetchingNextDistricts,
			isFetchingNextCities,
		],
	);

	return (
		<>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				width={PORTAL_STATUS_MODAL_WIDTH_CLASS}
				padding="p-6 sm:p-8"
				showCloseIcon
				title={modalTitle}>
				<div className="flex w-full flex-col">
					<div className="mb-4 flex justify-end">
						<Button
							onClick={handleAddNew}
							variant="tertiary"
							className="flex items-center gap-2 !border-accent !text-accent hover:!bg-accent-40"
							disabled={editingIndex !== null || !hasLoaded}>
							<Add fontSize="small" />
							{t("feature.status.actions.addEntity", { entity: title })}
						</Button>
					</div>

					{!hasLoaded && (
						<div className="flex items-center justify-center py-12">
							<span
								className="loader-spin h-8 w-8"
								role="progressbar"
								aria-label={t("feature.status.loading")}
							/>
							<span className="ml-3 text-gray-500">
								{t("feature.status.actions.loadingEntities", { entity: titleLower })}
							</span>
						</div>
					)}

					{hasLoaded && formDataList.length === 0 && (
						<div className="py-8 text-center text-gray-500">
							{t("feature.status.actions.emptyEntities", {
								entity: titleLower,
								entitySingular: title,
							})}
						</div>
					)}

					{hasLoaded && formDataList.length > 0 && (
						<div ref={formListRef} className="max-h-[60vh] overflow-y-auto pr-2">
							{formDataList.map(renderFormCard)}
						</div>
					)}

					<div className="mt-6 flex justify-end border-t pt-4">
						<Button
							text={t("feature.status.actions.close")}
							variant="tertiary"
							onClick={onClose}
							className="px-8 py-3"
						/>
					</div>
				</div>
			</Modal>

			<ConfirmationModal
				isOpen={deleteModalOpen}
				onClose={handleDeleteCancel}
				onConfirm={handleDeleteConfirm}
				title={t("feature.status.actions.deleteTitle", { entity: title })}
				description={t("feature.status.actions.deleteDescription", { entity: titleLower })}
				confirmText={t("feature.status.actions.delete")}
				cancelText={t("feature.status.actions.cancel")}
				loading={isDeleting}
				icon={
					<div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
						<Delete className="h-8 w-8 text-red-600" />
					</div>
				}
			/>
		</>
	);
};

export default StatusModal;
